import { useEffect, useRef, forwardRef } from 'react';
import { cn } from '@/utils';

const SelectBox = forwardRef(({
  headline,
  children,
  onClick,
  linkText,
  onLinkClick,
  isExpanded,
  showIcon,
  DropdownContent,
  onClickOutside,
  isDisabled,
  disabledClassName,
  id,
  isHighlighted,
  highlightedClassName,
  testid,
  buttonDataAttributes = {}
}, ref) => {
  const parentRef = useRef(null);

  const handleClickOutside = (e) => {
    if (parentRef.current && !parentRef.current.contains(e.target)) {
      onClickOutside();
    }
  };

  const handleLinkClick = () => {
    onLinkClick();
  };

  useEffect(() => {
    if (onClickOutside && isExpanded) {
      document.addEventListener('click', handleClickOutside, { once: true, capture: true });
    }
  }, [isExpanded]);

  return (
    <div id={id} className="mb-2xl" ref={ref}>
      <div className="flex justify-between">
        <label
          id="listbox-label"
          className="block font-semibold mb-2xs text-sm desktop:text-base"
        >
          {headline}
        </label>
        <If condition={linkText}>
          <a
            className="h-[1.2rem] text-[color:var(--theme-link-text-color-2)] font-bold inline-flex items-center whitespace-nowrap gap-4xs cursor-pointer"
            onClick={handleLinkClick}
          >
            <ft-icon slot="start" name="info"></ft-icon>
            <span className="text-sm desktop:text-base">{linkText}</span>
          </a>
        </If>
      </div>
      <div ref={parentRef} className="relative mt-5xs">
        <button
          data-testid={testid}
          type="button"
          aria-haspopup="listbox"
          aria-expanded="true"
          aria-labelledby="listbox-label"
          onClick={onClick}
          className={cn(
            'relative py-5xs pl-lg pr-5xl w-full h-[4.375rem] font-semibold text-sm bg-white border w-rounded',
            'desktop:text-base desktop:pr-6xl',
            isDisabled ? disabledClassName : '',
            isHighlighted ? highlightedClassName : 'border-neutral-medium',
            !showIcon || isDisabled ? 'cursor-default' : ''
          )}
          {...buttonDataAttributes}
        >
          {children}
          <If condition={showIcon}>
            <span className="flex absolute inset-y-0 right-0 items-center ml-2xs pointer-events-none pr-3xs desktop:pr-sm">
              <ft-icon name="dropdown" color="brand" height="36"></ft-icon>
            </span>
          </If>
        </button>
        <If condition={isExpanded && DropdownContent}>{DropdownContent}</If>
      </div>
    </div>
  );
});

export default SelectBox;
